<template>
    <div class="more-actions">
        <div class="more-actions__action" v-if="actions && actions.length > 0">
            <el-popover
                placement="bottom-start"
                :width="showCopiedToClipboard ? 155 : 350"
                :title="showCopiedToClipboard ? null : title"
                :visible-arrow="false"
                :popper-class="`smr-popper ${ showCopiedToClipboard ? 'smr-popper--show-arrow smr-popper--offset-left-large' : 'smr-popper--offset-left' }`"
                trigger="click"
                v-model="popperOpen"
                @show="popperShowHandler"
                @hide="popperHideHandler"
            >
                <div v-if="!showSharingActions" class="smr-popper__close" @click="popperCloseClickHandler">
                    <icon><IconClose/></icon>
                </div>
                <SharingActions v-if="showSharingActions" @copiedChange="copiedChangeHandler" @closePopup="popperCloseClickHandler" />
                <div v-else class="action-list">
                    <div class="action-list__item ui-center-vertically" v-for="action in actions" :key="action.event" @click="actionClickHandler(action.event, action.keepPopperOpen)">
                        <icon class="action-list__item__icon"><component :is="action.icon" /></icon>
                        <span class="action-list__item__label copy black">{{ action.label }}</span>
                    </div>
                </div>
               
                <div slot="reference" :class="{'more-actions__expand' : expandClickableArea}">
                    <SmrTooltip :content="tooltips.moreActions ? tooltips.moreActions.content: ''" :position="tooltips.moreActions ? tooltips.moreActions.position : ''" :zIndex="true">
                        <Icon class="more-actions__action__icon" :class="{'is-grey': smallIcon}" :width="iconSize" :height="iconSize">
                            <IconMore />
                        </Icon>
                    </SmrTooltip>
                </div>
            </el-popover>
            
        </div>
        <FavouriteButton class="more-actions__action" :favouriteObject="favouriteObject" v-if="favouriteObject" :tooltips="tooltips.favourites" :trackingData="trackingData"/>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import FavouriteButton from '@/components/FavouriteButton.vue'
import SmrTooltip from '@/components/SmrTooltip.vue'
import Icon from '@/components/Icon.vue'
import IconMore from '@/components/icons/IconMore.vue'
import IconPdf from '@/components/icons/IconPdf.vue'
import IconShare from '@/components/icons/IconShare.vue'
import IconExport from '@/components/icons/IconExport.vue'
import IconBell from '@/components/icons/IconBell.vue'
import IconClose from '@/components/icons/IconClose.vue'
import IconEdit from '@/components/icons/IconEdit.vue'
import IconDelete from '@/components/icons/IconDelete.vue'
import { trackEvent, trackGa4Event } from '@/utils/tracking'
import { moreActionsTracking } from '@/data/trackingInfo';
import eventBus from '@/utils/event-bus';
import SharingActions from '@/components/SharingActions.vue'

export default {
    components: {
        FavouriteButton,
        SmrTooltip,
        Icon,
        IconMore,
        IconPdf,
        IconShare,
        IconExport,
        IconBell,
        IconClose,
        IconEdit,
        IconDelete,
        SharingActions,
    },
    props: {
        favouriteObject: {
            type: [Object, Boolean],
            default: false
        },
        actions: {
            type: Array,
            default: () => []
        },
        title: {
            type: String,
            default: 'Weitere Optionen'
        },
        smallIcon: {
            type: Boolean,
            default: false
        },
        emitGeneralEvent: { // in case of this component being inside another general component
            type: Boolean,
            default: false
        },
        expandClickableArea: { // for bigger clickable area around dots
            type: Boolean,
            default: false
        },
        tooltips: {
            type: [Object, Boolean],
            default: false
        },
        trackingType: {
            type: [String, Boolean],
            default: false
        },
        trackingFavourite: {
            type: [String, Boolean],
            default: false
        },
        trackingName: {
            type: [String, Boolean],
            default: false
        },
        showSharingActions: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            popperOpen: false,
            showCopiedToClipboard: false,
        }
    },
    computed: {
        ...mapGetters(['mobile']),
        iconSize() {
            return this.smallIcon ? 18 : 24;
        },
        trackingData() {
            if(this.trackingFavourite) {
                return moreActionsTracking(this.trackingFavourite, null, true)  
            }
            return false
        },
    },
    mounted() {
        this.addListeners()
    },
    beforeDestroy() {
        this.removeListeners()
    },
    methods: {
        actionClickHandler(eventName, keepPopperOpen) {
            if(this.emitGeneralEvent) {
                this.$emit('actionClick', eventName);
            } else {
                this.$emit(eventName);
            }
            if(!keepPopperOpen) this.popperOpen = false;

            if(this.trackingType) {
                trackEvent(moreActionsTracking(this.trackingType, eventName, false));
                trackGa4Event(moreActionsTracking(this.trackingType, eventName, false, this.trackingFavourite));
            }
        },
        popperShowHandler() {
            eventBus.$emit('more-actions-opened', this.$el)
            if(this.mobile) {
                this.$store.commit('setDim', true)
            }
        },
        popperHideHandler() {
            this.$emit('moreActionsPopperClosed')
            if(this.mobile) {
                this.$store.commit('setDim', false)
            }
        },
        popperCloseClickHandler() {
            this.popperOpen = false;
        },
        moreActionsOpenedHandler(el) {
            if(this.$el !== el) {
                this.popperOpen = false
            }
        },
        copiedChangeHandler(value) {
            this.showCopiedToClipboard = value
        },
        addListeners() {
            eventBus.$on('more-actions-opened', this.moreActionsOpenedHandler)
        },
        removeListeners() {
            eventBus.$off('more-actions-opened', this.moreActionsOpenedHandler)
        },
    }
}
</script>

<style lang="scss" scoped>
.more-actions {
    $block: &;
    display: flex;
    flex-shrink: 0;
    color: $color-grey-dark;
    &__expand {
        padding: 5px;
        .no-touch &:hover {
            .is-grey {
                color: $color-black;
            }
        }
    }
    &__action {
        cursor: pointer;
        padding: 0 6px;
        line-height: 1;
        &:first-child {
            padding-left: 0;
        }
        &:last-child {
            padding-right: 0;
        }
        .no-touch &:hover {
            color: $color-black;
        }
        &__icon {
            &.is-grey {
                color: $color-grey;
                .no-touch &:hover {
                    color: $color-black;
                }
            }
        }
    }
}
</style>